import { useState } from "react";
import { toast } from "sonner";
import { X, Wallet } from "lucide-react";
import { api, formatMoney } from "@/lib/api";

const QUICK = ["Milk", "Sugar", "Tea powder", "Gas", "Snacks stock", "Cleaning"];

export default function AddExpenseModal({ outlet, onClose, onAdded }) {
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!description.trim() || !(amt > 0)) {
      toast.error("Enter a description and an amount above ₹0");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/expenses", { outlet_id: outlet.id, description, amount: amt });
      toast.success(`Expense saved · ₹${formatMoney(data.amount)}`);
      onAdded?.(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save expense");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4" onClick={onClose} data-testid="expense-modal">
      <form onSubmit={submit} className="readable bg-card border border-border w-full max-w-sm animate-fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet strokeWidth={1.75} className="w-5 h-5 text-primary" />
            <div>
              <div className="text-lg font-bold leading-tight">Add expense</div>
              <div className="text-xs text-muted-foreground">{outlet.name} · deducted from today's cash</div>
            </div>
          </div>
          <button type="button" data-testid="expense-modal-close" onClick={onClose} className="p-1 hover:bg-muted">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-sm font-semibold">What was it for?</span>
            <input
              data-testid="expense-description-input"
              autoFocus
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Milk 10L"
              className="mt-1 w-full bg-background border border-border px-3 py-2.5 text-base focus:outline-none focus:border-primary"
            />
          </label>
          <div className="flex flex-wrap gap-1.5">
            {QUICK.map((q) => (
              <button
                key={q}
                type="button"
                data-testid={`expense-quick-${q.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setDescription(q)}
                className="px-2.5 py-1 text-xs border border-border bg-background hover:border-primary hover:text-primary transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
          <label className="block">
            <span className="text-sm font-semibold">Amount (₹)</span>
            <input
              data-testid="expense-amount-input"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="mt-1 w-full bg-background border border-border px-3 py-2.5 text-2xl font-bold tabular-nums focus:outline-none focus:border-primary"
            />
          </label>
        </div>
        <div className="border-t border-border p-4 grid grid-cols-2 gap-2">
          <button type="button" data-testid="expense-cancel-btn" onClick={onClose} className="border border-border py-3 text-sm font-semibold hover:bg-muted">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="expense-save-btn"
            disabled={busy}
            className="bg-primary text-primary-foreground py-3 text-sm font-bold hover:opacity-90 active:scale-[0.98] transition-transform disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save expense"}
          </button>
        </div>
      </form>
    </div>
  );
}
